import React from 'react';

import cn from 'classnames/bind';
import { Radio } from '@consta/uikit/Radio';
import { useChoiceGroup } from '@consta/uikit/useChoiceGroup';
import styles from './styles.styl';
import { PropsWithHTMLAttributesAndRef } from '@consta/uikit/__internal__/src/utils/types/PropsWithHTMLAttributes';

const cx = cn.bind(styles);

export const radioGroupDirections = ['column', 'row'] as const;
export type RadioGroupDirection = typeof radioGroupDirections[number];
export const radioGroupDefaultDirection: RadioGroupDirection =
  radioGroupDirections[0];

export const radioGroupSizes = ['m', 'l'] as const;
export type RadioGroupPropSize = typeof radioGroupSizes[number];
export const radioGroupDefaultSize: RadioGroupPropSize = radioGroupSizes[0];

export const radioGroupViews = ['primary', 'ghost'] as const;
export type RadioGroupPropView = typeof radioGroupViews[number];
export const radioGroupDefaultView: RadioGroupPropView = radioGroupViews[0];

export const radioGroupPropAlign = ['center', 'top'] as const;
export type RadioGroupPropAlign = typeof radioGroupPropAlign[number];
export const radioGroupPropAlignDefault: RadioGroupPropAlign =
  radioGroupPropAlign[0];

type CommonProps<ITEM> = {
  align?: RadioGroupPropAlign;
  value?: ITEM | null;
  items: ITEM[];
  getLabel: (item: ITEM) => string;
  getKey: (item: ITEM) => number;
  getDisabled?: (item: ITEM) => boolean | undefined;
  onChange: (props: {
    e: React.ChangeEvent<HTMLInputElement>;
    value: ITEM;
  }) => void;
  name?: string;
  direction?: RadioGroupDirection;
  size?: RadioGroupPropSize;
  view?: RadioGroupPropView;
  disabled?: boolean;
  className?: string;
};

type Props<ITEM> = PropsWithHTMLAttributesAndRef<
  CommonProps<ITEM>,
  HTMLDivElement
>;

type RadioGroup = <ITEM>(props: Props<ITEM>) => React.ReactElement | null;

// eslint-disable-next-line react/display-name
export const CustomRadioGroup: RadioGroup = React.forwardRef((props, ref) => {
  const {
    value = null,
    items,
    getLabel,
    getKey,
    getDisabled,
    onChange,
    name,
    direction = radioGroupDefaultDirection,
    size = radioGroupDefaultSize,
    view = radioGroupDefaultView,
    align = radioGroupPropAlignDefault,
    disabled = false,
    className,
    ...otherProps
  } = props;

  const { getOnChange, getChecked } = useChoiceGroup({
    value,
    getKey: getKey,
    callBack: onChange,
    multiple: false,
  });

  return (
    <div
      {...otherProps}
      ref={ref}
      className={cx(
        'CustomRadioGroup',
        `CustomRadioGroup__${direction}`,
        `CustomRadioGroup__${size}`,
        `CustomRadioGroup__${view}`,
        className,
      )}
    >
      {items.map((item) => (
        <Radio
          align={align}
          key={getKey(item)}
          label={getLabel(item)}
          size={size}
          view={view}
          name={name}
          disabled={disabled || (!!getDisabled && getDisabled(item))}
          checked={getChecked(item)}
          onChange={({ e }) => getOnChange(item)(e)}
          className={cx(`CustomRadioGroup__${direction}_item`)}
        />
      ))}
    </div>
  );
});
